import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processInvitation = async () => {
      const token = searchParams.get("token");
      const shouldExplore = searchParams.get("explore") === "true";

      if (!token) {
        navigate("/auth");
        return;
      }

      try {
        // Track the acceptance
        const { data, error } = await supabase.functions.invoke(
          "track-invitation-response",
          {
            body: {
              tracking_token: token,
              action_type: "accepted",
            },
          }
        );

        if (error) throw error;

        if (data?.invitation) {
          // Store invitation data in session storage with redirect target
          const invitationWithRedirect = {
            ...data.invitation,
            redirect_after_signup: getRedirectTarget(data.invitation),
          };
          sessionStorage.setItem("invitation_data", JSON.stringify(invitationWithRedirect));
          
          // If explore flag is set, go through intro flow first
          if (shouldExplore) {
            sessionStorage.setItem("from_invitation_explore", "true");
            
            // Track that intro flow has started
            await supabase.functions.invoke("track-invitation-response", {
              body: {
                tracking_token: token,
                action_type: "intro_started",
              },
            });
            
            navigate("/intro");
          } else {
            // Direct signup
            navigate(`/signup-from-invitation?token=${token}`);
          }
        } else {
          throw new Error("Invalid invitation");
        }
      } catch (error) {
        console.error("Error processing invitation:", error);
        navigate("/auth");
      } finally {
        setIsProcessing(false);
      }
    };

    processInvitation();
  }, [searchParams, navigate]);

  // Determine where to redirect after signup based on invitation type
  const getRedirectTarget = (invitation: any): string => {
    if (invitation.channel_id) {
      // Get channel slug from invitation or default to channel page
      const channelSlug = invitation.channel_slug || invitation.channel_name?.toLowerCase().replace(/\s+/g, '-');
      if (channelSlug === 'curt' || invitation.channel_name === 'CURT') return '/curt-channel';
      if (channelSlug === 'ispai' || invitation.channel_name === 'iSPAI') return '/ispai-channel';
      if (channelSlug === 'oil-and-gas' || channelSlug === 'oil-gas' || invitation.channel_name === 'Oil and Gas' || invitation.channel_name === 'Oil & Gas') return '/oil-gas-channel';
      if (channelSlug === 'us-federal-government' || channelSlug === 'usa-gsa' || invitation.channel_name === 'US Federal Government' || invitation.channel_name === 'USA/GSA') return '/usa-channel';
      return `/channel/${channelSlug}`;
    }
    // Default to creators page
    return '/creators';
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-lg">Processing your invitation...</p>
      </div>
    </div>
  );
};

export default AcceptInvitation;
